import { Button } from '@w7/ui'
import { claims, logout } from '@/auth/oidc'
import { STRIX_LOGO_SRC } from './StrixMark'
import './auth.css'

function playerPortalUrl(): string {
  const raw = (import.meta.env.VITE_PLAYER_PORTAL_URL as string | undefined)?.trim()
  return (raw || 'http://localhost:8080').replace(/\/$/, '')
}

/**
 * Shown when the signed-in user lacks console roles (agent / ops / risk-desk).
 *
 * Important: agents and members both carry `agentId`. Do NOT treat agentId alone
 * as “this is a member” — a Master stuck on this page is usually a Keycloak SSO
 * session from a prior member login, or a missing `agent` role. Always offer
 * Back to login (clears SSO). Player portal is a secondary option only.
 */
export function NoAccessPage() {
  const who = claims()
  const label = who?.preferredUsername || who?.email || who?.sub || 'this account'
  const portal = playerPortalUrl()
  // Hierarchy-bound but no console role — may be a member, or an agent SSO mix-up.
  const hierarchyBound = Boolean(who?.agentId)

  return (
    <div className="sm-login" data-testid="no-access">
      <div className="sm-login-bg" aria-hidden="true" />
      <div className="sm-login-card">
        <div className="sm-login-brand">
          <img src={STRIX_LOGO_SRC} alt="" width={40} height={40} decoding="async" />
          <div>
            <strong>Book Maker</strong>
            <span>Ops console</span>
          </div>
        </div>
        <h1>No access</h1>
        <p className="sm-login-lede" role="status" data-testid="no-access-message">
          {label} cannot use this console. Only agents (levels 1–5) and ops roles can.
          {hierarchyBound ? (
            <>
              {' '}
              If this is a <strong>member</strong> account, use the{' '}
              <a className="w7-link" href={portal} data-testid="player-portal-link">
                player portal
              </a>
              . If you meant to sign in as an agent, go back to login and choose that account
              (Keycloak may still be signed in as a member).
            </>
          ) : (
            <> Contact support if this is unexpected.</>
          )}
        </p>
        <div className="sm-login-form">
          <Button
            variant="primary"
            data-testid="back-to-login"
            onClick={() => logout({ silent: true })}
          >
            Back to login
          </Button>
          {hierarchyBound ? (
            <Button
              variant="secondary"
              data-testid="open-player-portal"
              onClick={() => {
                window.location.assign(portal)
              }}
            >
              Open player portal
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  )
}
